import type { Manifest, ManifestIntent, ManifestType } from '../api/authoring'
import { actionLabel, humanise } from './labels'
import { asSchema, holds, isFormable, orderedProperties, structuralOf, type Schema, type Structural, type StructuralRole } from './schema'

// Everything the client knows about types, it reads from here.
//
// The palette, the containment rules, the command vocabulary, the slot
// vocabulary and now the *schemas* all come off
// `/v1/authoring/manifest`, so a newly registered type appears in the
// admin with no client change. A hardcoded type list fails SPEC's
// acceptance checklist outright, and a hardcoded containment matrix would
// silently drift from the build's actual registry.
//
// The schemas are what closed the last gap. Before them a type's
// *structure* was derived and its *form* was hand-written, so a type
// nobody had written a form for was placeable and unreadable — which is
// what `FallbackEditor` used to say out loud. Now both halves come off
// the wire and the hand-written editors are the exception rather than
// the mechanism.

export function typeOf(manifest: Manifest | undefined, name: string): ManifestType | undefined {
  return manifest?.types.find((entry) => entry.name === name)
}

/**
 * What a type is called, in words, with a fallback for a type whose
 * prose nobody has written.
 *
 * **The words come off the wire.** A table here keyed by type name would
 * be a second declaration of something the type already states, and the
 * failure is not hypothetical: a type absent from such a table renders
 * as `custom/practice` to a site owner, and a type a tenant declared
 * this morning can never be in it. `humanise` is what the header of
 * `labels.ts` always said it would become — the fallback, not the
 * mechanism.
 *
 * The plural falls back to the title rather than to an invented one.
 * English does not pluralise by rule ("Photo galleries" is not "Photo
 * gallerys"), and a heading reading "Photo gallery" where it means
 * several is a smaller error than a heading reading a word that is not
 * one.
 */
export interface TypeProse {
  title: string
  plural: string
  description?: string
  /** False where nothing declared these and they were derived from the name. */
  declared: boolean
}

export function typeProse(manifest: Manifest | undefined, name: string): TypeProse {
  return proseOf(typeOf(manifest, name), name)
}

/** The same, for a type entry already in hand. */
export function proseOf(declared: ManifestType | undefined, name?: string): TypeProse {
  const prose = declared?.prose
  const fallback = humanise(readable(declared?.name ?? name ?? ''))
  if (!prose) return { title: fallback, plural: fallback, description: undefined, declared: false }
  return {
    title: prose.title,
    plural: prose.plural ?? prose.title,
    description: prose.description,
    declared: true,
  }
}

/**
 * A type name with the part that is addressing rather than naming taken
 * off, for the fallback only.
 *
 * A tenant-defined type is named `custom/card`, and the prefix is there
 * to keep a tenant from shadowing a platform type — it is a fact about
 * the namespace, not about the thing. Humanising the whole string gives
 * "Custom/card", which is worse than the wire name it came from.
 *
 * This is the fallback path and only the fallback path: a type that
 * declares its own words never reaches here.
 */
function readable(name: string): string {
  const local = name.slice(name.indexOf('/') + 1)
  return local || name
}

/** The display name for one of them — the commonest of the three by far. */
export function typeTitle(manifest: Manifest | undefined, name: string): string {
  return typeProse(manifest, name).title
}

/**
 * The words a control offering one intent uses.
 *
 * The declaration first, and `actionLabel` behind it. That order is the
 * whole of §3.2 applied one level down: a type says "Add a photo" and a
 * client that had to derive it from `gallery.image_added` says "Add
 * image", which is the model's word for the thing rather than the
 * author's.
 */
export function intentTitle(manifest: Manifest | undefined, typeName: string, intent: string): string {
  return intentOf(manifest, typeName, intent)?.title ?? actionLabel(intent)
}

/** The same, for an intent already in hand — the manifest entry *is* the declaration. */
export function offerLabel(intent: ManifestIntent): string {
  return intent.title ?? actionLabel(intent.name)
}

/**
 * The types that may be placed inside this one. A node's palette is
 * exactly this list.
 */
export function containable(manifest: Manifest | undefined, name: string): string[] {
  return typeOf(manifest, name)?.contains ?? []
}

/** Whether a type may be defined in place, or must be linked. */
export function inlineable(manifest: Manifest | undefined, name: string): boolean {
  return typeOf(manifest, name)?.inlineable ?? false
}

/**
 * Whether a component of this type holds a stored file, and may therefore
 * be the target of an upload.
 *
 * **This is how a client offers an uploader: by asking, never by knowing
 * which type is the file type.** It is the same rule the palette and the
 * containment matrix follow, and it is what keeps a type that holds files
 * from becoming a name written into this client — a second one gets the
 * uploader the day it is registered.
 *
 * The intent that records a landed file is deliberately absent from that
 * type's `intents`, because every member of its payload is something the
 * platform reads off the object. So the capability is advertised in one
 * place and the command in none, which is exactly the split: a client can
 * see that a component holds a file and cannot say what the file is.
 */
export function acceptsUpload(manifest: Manifest | undefined, name: string): boolean {
  return typeOf(manifest, name)?.acceptsUpload ?? false
}

/**
 * Whether **this document** holds a file — the question anything drawing
 * an uploader has to ask.
 *
 * `acceptsUpload` is the type's answer and is the right one for finding
 * which type a picture is; it is the wrong one for a control, because a
 * type may hold a file for only some of its documents. A Media is
 * internal or external and only the internal kind has bytes of ours
 * behind it, so a create dialog promising "the file comes next" over an
 * external one, and an editor drawing an uploader beside it, were both
 * offering a step the surface refuses a grant for.
 *
 * The condition is the manifest's, read against the document's own
 * fields. Nothing here names a type or a kind: a second type that holds
 * a file under some condition of its own is answered the day it is
 * registered.
 */
export function uploadsInto(
  manifest: Manifest | undefined,
  name: string,
  fields: Record<string, unknown> | undefined,
): boolean {
  const declared = typeOf(manifest, name)
  if (!declared?.acceptsUpload) return false
  return holds(declared.uploadWhen, fields ?? {})
}

/**
 * The fields that make a new document of this type one that holds a
 * file.
 *
 * Read off `uploadWhen`, which is the manifest saying which of a type's
 * documents take an upload. The picker used to write `kind: 'internal'`
 * itself, which was a field and a value of Media named in the client: a
 * second type that holds a file under another condition would have been
 * created unable to take one.
 */
export function uploadingFields(manifest: Manifest | undefined, name: string): Record<string, unknown> {
  const when = typeOf(manifest, name)?.uploadWhen
  return when && when.is.length > 0 ? { [when.field]: when.is[0] } : {}
}

/**
 * Whether some documents of this type hold no file of ours — a Media
 * pointing at an address hosted elsewhere. Those are made by stating
 * their fields, not by uploading.
 */
export function holdsFilesElsewhere(manifest: Manifest | undefined, name: string): boolean {
  return typeOf(manifest, name)?.uploadWhen !== undefined
}

/**
 * The slots a client should offer for a type, in reading order.
 *
 * **Absent is not empty.** A type with no `slots` does not arrange named
 * regions at all, so a caller must not read the absence as "this type
 * has regions and there are none" and draw an empty slot picker.
 */
export function declaredSlots(manifest: Manifest | undefined, name: string): string[] | undefined {
  return typeOf(manifest, name)?.slots
}

/**
 * The slots to show for a node: what the type declares, plus anything
 * already stored that it does not.
 *
 * The union rather than the declaration alone, because the declaration
 * is **advisory** — nothing refuses an undeclared slot, so a document may
 * legitimately hold one, and a client that showed only declared regions
 * would hide content rather than reveal a mistake. Declared order first,
 * since a type means header before body before footer; undeclared ones
 * follow, sorted, and are flagged where they are drawn.
 */
export function slotsToShow(
  manifest: Manifest | undefined,
  typeName: string,
  stored: string[],
): Array<{ slot: string; declared: boolean }> {
  const declared = declaredSlots(manifest, typeName) ?? []
  const storedSet = new Set(stored)
  const extra = stored.filter((slot) => !declared.includes(slot)).sort()

  return [
    ...declared.map((slot) => ({ slot, declared: true })),
    ...extra.map((slot) => ({ slot, declared: false })),
  ].filter((entry) => entry.declared || storedSet.has(entry.slot))
}

/**
 * Whether a type declares an intent — what makes a control offerable.
 *
 * The manifest lists what may be **commanded**, which is narrower than
 * what the platform can decode: `component.child_detached` is a declared
 * event and deliberately absent because its copy must be read by the
 * platform. A client that offered it from a hardcoded list would hand an
 * author a control that could quietly corrupt a placement.
 */
export function declaresIntent(manifest: Manifest | undefined, typeName: string, intent: string): boolean {
  return Boolean(intentOf(manifest, typeName, intent))
}

export function coreIntent(manifest: Manifest | undefined, intent: string): boolean {
  return Boolean(manifest?.coreIntents.some((declared) => declared.name === intent))
}

/**
 * One intent as the manifest declares it — its name and its payload
 * schema — looked up in the type's own list and then the core's.
 *
 * The core is searched second and unconditionally, because a structural
 * intent applies to every type and belongs to none: a caller asking
 * whether a Page may be pinned is asking about the core's vocabulary
 * while naming a type, and requiring it to know which list to look in
 * would put that split in every call site.
 */
export function intentOf(
  manifest: Manifest | undefined,
  typeName: string,
  intent: string,
): ManifestIntent | undefined {
  const declared = typeOf(manifest, typeName)?.intents.find((entry) => entry.name === intent)
  return declared ?? manifest?.coreIntents.find((entry) => entry.name === intent)
}

/** The schema of what a document of this type holds. */
export function fieldsSchema(manifest: Manifest | undefined, typeName: string): Schema | undefined {
  return asSchema(typeOf(manifest, typeName)?.fields)
}

/** The schema of what may be sent with one intent. */
export function payloadSchema(
  manifest: Manifest | undefined,
  typeName: string,
  intent: string,
): Schema | undefined {
  return asSchema(intentOf(manifest, typeName, intent)?.payload)
}

/**
 * The type's intents that this client can draw a complete form for.
 *
 * **Computed, never listed.** An intent qualifies when its payload names
 * no part of the component tree — see `namesTheTree` — which is a fact
 * the payload schema carries rather than one this client remembers per
 * type. That is the whole reason a tenth component type is editable the
 * day it is registered: its field intents land here automatically, and
 * its structural ones fall through to the placement controls, without
 * anybody deciding which is which.
 *
 * The core's structural intents are deliberately absent: they are not a
 * type's own vocabulary, they all name a placement, and pin already has
 * a control that says far more than a generated form could.
 */
export function formableIntents(
  manifest: Manifest | undefined,
  typeName: string,
  fields: Record<string, unknown> | undefined,
): ManifestIntent[] {
  return applicableIntents(manifest, typeName, fields).filter((intent) => {
    const schema = asSchema(intent.payload)
    return schema ? isFormable(schema, schema) : false
  })
}

/**
 * The type's intents that apply to a node holding `fields`: every one
 * whose `when` the fields satisfy, and every one that declares none.
 *
 * **Required, and `undefined` only where no document is in hand.** An
 * intent the platform declares for some of a type's documents is refused
 * on the rest, so a form drawn for it there is a box whose only outcome
 * is a message — an uploaded picture offered "Point at a file hosted
 * elsewhere". The argument is not optional so that no screen can forget
 * it; the static coverage check, which asks about a type rather than a
 * document, says so by passing `undefined`.
 */
export function applicableIntents(
  manifest: Manifest | undefined,
  typeName: string,
  fields: Record<string, unknown> | undefined,
): ManifestIntent[] {
  const intents = typeOf(manifest, typeName)?.intents ?? []
  return fields === undefined ? intents : intents.filter((intent) => holds(intent.when, fields))
}

/** One structural intent, with the name a control sends it under. */
export interface StructuralIntent extends Structural {
  name: string
  /** The words a control offers it under, declared or derived. */
  title: string
  payload: Schema
}

/**
 * The type's structural intents, taken apart into what a control fills
 * and what an author does.
 *
 * **This is what replaced the hardcoded triple.** `PageEditor` used to
 * name `page.block_placed`, `page.block_removed` and
 * `page.slot_reordered` outright, which was honest while exactly one
 * screen placed anything — and became the per-type coupling the registry
 * exists to remove the moment a second type needed the same three
 * controls under different names. A screen now asks for the role and
 * sends whatever the type calls it.
 *
 * The core's structural intents stay out, as they do for `formable`: pin
 * and unpin are the same shape as an `act` and already have a control
 * that says far more about a revision than a derived one could.
 */
export function structuralIntents(
  manifest: Manifest | undefined,
  typeName: string,
): StructuralIntent[] {
  return (typeOf(manifest, typeName)?.intents ?? []).flatMap((intent) => {
    const payload = asSchema(intent.payload)
    if (!payload) return []
    const structural = structuralOf(payload, payload)
    return structural ? [{ name: intent.name, title: offerLabel(intent), payload, ...structural }] : []
  })
}

/**
 * The type's structural intents in one role — the question a control
 * actually asks.
 *
 * A type may legitimately declare two of a role and this returns both:
 * Website has `website.page_added` and `website.default_placed`, which
 * place into different arrangements of the same component. A caller that
 * wants one asks by the slot or the parent it is placing into, never by
 * position in this list.
 */
export function intentsInRole(
  manifest: Manifest | undefined,
  typeName: string,
  role: StructuralRole,
): StructuralIntent[] {
  return structuralIntents(manifest, typeName).filter((intent) => intent.role === role)
}

/**
 * The one intent in a role that arranges a *named region*, or the one
 * that arranges a flat list.
 *
 * This is the only non-arbitrary way to choose when a type declares two
 * of a role, and Website is why it has to be asked rather than assumed:
 * it places into its slot defaults and into its routing tree, and the
 * difference between those two intents is exactly that one carries a
 * slot. A screen knows which it is drawing, so it says.
 */
export function intentArranging(
  manifest: Manifest | undefined,
  typeName: string,
  role: StructuralRole,
  keyed: boolean,
): StructuralIntent | undefined {
  const inRegion = intentsInRole(manifest, typeName, role).filter((intent) => Boolean(intent.slot) === keyed)
  if (role !== 'act') return inRegion[0]
  return inRegion.find(sendsWhole)
}

/**
 * Whether a control can send this intent with nothing from the author.
 *
 * **A row action is one press, so it can only be an intent with nothing
 * left to fill in.** Grid is why this is asked: it declares
 * `grid.cell_configured` and `grid.cell_removed`, both name one cell, and
 * the manifest lists them alphabetically. Taking the first put "Set how
 * wide this block sits" on the row's delete action, and pressing it sent
 * a configure carrying no span and no alignment, which reset both and
 * removed nothing.
 *
 * An intent that leaves members to the author is a form over a
 * placement, and an editor draws it. This does not recover the verb —
 * `structuralOf` is right that a payload cannot say one — so two intents
 * that both name a placement and nothing else are still told apart only
 * by a screen that names them. `docs/blockers.md` B21 holds the
 * declaration that would end that.
 */
function sendsWhole(intent: StructuralIntent): boolean {
  return intent.rest.length === 0 && intent.unsupported.length === 0
}

/** What a control fills in on the author's behalf. */
export interface Supplied {
  child?: unknown
  link?: string
  order?: string[]
  slot?: string
  /** The region a placement is leaving, on a `move`. */
  fromSlot?: string
  position?: number
}

/**
 * Assemble a structural intent's payload from the members a control
 * supplies and the ones an author filled in.
 *
 * **This function is the mechanism.** Every value goes in under the name
 * the *marker* found, never under a name written here, so a type spelling
 * its member `page` rather than `linkId` gets the same control — which is
 * the difference between a client that works for the nine types and one
 * that works for the tenth.
 *
 * A supplied value the intent does not declare is dropped rather than
 * sent: `website.not_found_placed` replaces rather than inserts, so it
 * has no position, and a control that appended one would be stating
 * something the fold never asked for. `DecodeIntent` refuses unknown
 * fields strictly, so it would also simply fail.
 */
export function structuralPayload(
  intent: StructuralIntent,
  supplied: Supplied,
  rest: Record<string, unknown> = {},
): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...rest }
  const write = (member: string | undefined, value: unknown) => {
    if (member !== undefined && value !== undefined) payload[member] = value
  }
  write(intent.child, supplied.child)
  write(intent.link, supplied.link)
  write(intent.order, supplied.order)
  write(intent.slot, supplied.slot)
  write(intent.fromSlot, supplied.fromSlot)
  write(intent.position, supplied.position)
  return payload
}

/**
 * The members an intent's payload states. For a field intent, which every
 * tenant type's intent is, these are the fields it writes.
 */
export function intentWrites(intent: ManifestIntent): string[] {
  const schema = asSchema(intent.payload)
  return schema ? Object.keys(schema.properties ?? {}) : []
}

/**
 * Intents sorted by the first field each one writes, in the order the
 * type's schema lists its fields: how a site's owner reads a form, top to
 * bottom, rather than as the manifest sorts intent names. An intent
 * writing nothing the schema names goes after the rest, in its place.
 */
export function inFieldOrder(manifest: Manifest | undefined, typeName: string, intents: ManifestIntent[]): ManifestIntent[] {
  const schema = fieldsSchema(manifest, typeName)
  const order = new Map((schema ? orderedProperties(schema, schema) : []).map((property, index) => [property.name, index]))
  const rank = (intent: ManifestIntent) => Math.min(Infinity, ...intentWrites(intent).map((field) => order.get(field) ?? Infinity))
  return [...intents].sort((left, right) => rank(left) - rank(right))
}
